/**
 * The pick, in full — the one card the CTA names.
 *
 * The same card carries a suggestion, today's scheduled workout, a missed
 * session or the plan's next. Only the tag and the reason change, so a reader
 * learns one shape and reads every frame with it.
 *
 * Layered on purpose: a plain sentence for a beginner, a mono-numbered receipt
 * under it for a sceptic, and the figure beside both for anyone who would
 * rather look than read.
 */

import { CalendarDaysIcon } from '@heroicons/react/24/outline';

import {
  cardMeta,
  figureOf,
  figureTitle,
} from '@personal-trainer-app/shared/src/utils/workout/start-options';

import type { WorkoutPickCardProps } from './StartWorkoutSheet.types';

import { Badge } from '@/components/ui/Badge';
import { CopyRuns } from '@/components/ui/CopyRuns';
import { MuscleFigure } from '@/components/ui/MuscleMap';
import { Text } from '@/components/ui/primitives';
import { Radio } from '@/components/ui/Radio';

export function WorkoutPickCard({
  workout,
  reason,
  name,
  onSelect,
}: WorkoutPickCardProps): React.JSX.Element {
  const { model, face } = figureOf(workout);
  const icon =
    reason.icon === 'calendar' ? (
      <CalendarDaysIcon className="h-4 w-4" />
    ) : null;

  return (
    <label
      className="flex cursor-pointer gap-3 rounded-lg border-[1.5px] border-stroke-accent bg-sakura-wash py-3.5 pl-4 pr-3.5"
      data-testid="start-workout-pick-card"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          {reason.tag && (
            <Badge
              variant={reason.tagTone === 'warning' ? 'warning' : 'primary'}
              appearance="soft"
              size="sm"
              caps
              pill
            >
              {reason.tag}
            </Badge>
          )}
          {reason.kind && (
            <Badge variant="default" appearance="soft" size="sm" caps pill>
              {reason.kind}
            </Badge>
          )}

          <Radio
            name={name}
            value={String(workout.id)}
            checked
            onChange={() => onSelect(workout.id)}
            aria-label={workout.name}
            className="ml-auto"
          />
        </div>

        <Text
          as="div"
          variant="heading-sm"
          className="mt-2 font-bold leading-tight"
        >
          {workout.name}
        </Text>

        <Text as="div" variant="body-sm" color="muted" className="mt-1">
          <CopyRuns
            segments={cardMeta(workout)}
            figureClassName="text-neutral-300"
          />
        </Text>

        <div className="mt-3 flex items-start gap-2">
          {icon && (
            <span className="mt-0.5 flex-none text-primary-300">{icon}</span>
          )}
          <Text
            as="p"
            variant="body-md"
            color="secondary"
            className="leading-snug"
          >
            <CopyRuns segments={reason.sentence} />
          </Text>
        </div>

        {reason.receipt && (
          <Text
            as="p"
            variant="body-sm"
            color="muted"
            /* Indented under the sentence only when an icon holds a gutter open. */
            className={`mt-1.5 leading-snug ${icon ? 'ml-6' : ''}`}
          >
            <CopyRuns segments={reason.receipt} />
          </Text>
        )}

        {reason.caution && (
          <div className="mt-2 flex items-start gap-2">
            <span
              aria-hidden="true"
              className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-warning-400"
            />
            <Text
              as="p"
              variant="body-sm"
              color="secondary"
              className="leading-snug"
            >
              <CopyRuns segments={reason.caution} />
            </Text>
          </div>
        )}
      </div>

      <div className="w-[52px] flex-none self-center">
        <MuscleFigure
          face={face}
          model={model}
          selectedId={null}
          interactive={false}
          onSelect={() => undefined}
          title={figureTitle(workout)}
        />
      </div>
    </label>
  );
}

export default WorkoutPickCard;
